/* eslint-disable no-plusplus, consistent-return, no-restricted-globals, default-case, prefer-const, prefer-destructuring */
import { amountDisplay } from '../formats'

const amountKeys = [
  'firstPurchaseAmount',
  'secondPurchaseAmount',
  'thirdPurchaseAmount',
  'fourthPurchaseAmount',
  'fifthPurchaseAmount',
  'sixthPurchaseAmount',
  'allSubsequentPurchaseAmount',
]

const percentageKeys = [
  'firstPurchasePercentage',
  'secondPurchasePercentage',
  'thirdPurchasePercentage',
  'fourthPurchasePercentage',
  'fifthPurchasePercentage',
  'sixthPurchasePercentage',
  'allSubsequentPurchasePercentage',
]

export const percentageOff = (price, percentage) => {
  const newPrice = price - price * (percentage / 100)
  return newPrice > 0 ? newPrice : 0
}

export const amountOff = (price, amount) => {
  const newPrice = price - amount
  return newPrice > 0 ? newPrice : 0
}

export const subsequentDiscount = (price, currencyCode, amounts) => {
  const { allSubsequentPurchasePercentage = 0, allSubsequentPurchaseAmount = 0 } = amounts
  let discountAmount
  let discountedPrice
  if (amounts && amounts.discountBy === 'Percentage') {
    const discountPrice = percentageOff(Number(price), allSubsequentPurchasePercentage)
    discountAmount = `- ${allSubsequentPurchasePercentage || 0}%`
    discountedPrice = amountDisplay(discountPrice, currencyCode)
  } else {
    discountAmount = `- ${amountDisplay(allSubsequentPurchaseAmount, currencyCode)}`
    const discountPrice = amountOff(Number(price), allSubsequentPurchaseAmount)
    discountedPrice = amountDisplay(discountPrice, currencyCode)
  }

  return { discountAmount, discountedPrice }
}

export const calculateProductDiscounts = (price, currencyCode, amounts, type, subType) => {
  const discounts = []
  const list = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth']
  const typeBy = amounts.discountBy

  if (type === 'Bundle' || (type === 'Standard' && subType === 'MultipleQuantity')) {
    list.forEach(l => {
      const amount = amounts[`${l}Purchase${typeBy}`]
      if (amount) {
        if (typeBy === 'Percentage') {
          const discountPrice = percentageOff(price, amount)
          discounts.push({
            discountAmount: `- ${amount || 0}%`,
            discountedPrice: amountDisplay(`${discountPrice}`, currencyCode),
          })
        }
        if (typeBy === 'Amount') {
          const discountPrice = amountOff(price, amount)
          discounts.push({
            discountAmount: `- ${amountDisplay(amount, currencyCode)}`,
            discountedPrice: amountDisplay(`${discountPrice}`, currencyCode),
          })
        }
      }
    })
  } else {
    // if typeBy is undefined, it means the current discounts is a draft and does not have amounts set up.
    if (typeBy) {
      const amount = amounts[typeBy.toLowerCase()]
      let discountAmount = ''
      let discountedPrice = ''
      if (typeBy === 'Percentage') {
        discountAmount = `- ${amount}%`
        discountedPrice = amountDisplay(`${percentageOff(price, amount)}`, currencyCode)
      }
      if (typeBy === 'Amount') {
        discountAmount = `- ${amountDisplay(amount, currencyCode)}`
        discountedPrice = amountDisplay(`${amountOff(price, amount)}`, currencyCode)
      }

      discounts.push({
        discountAmount,
        discountedPrice,
      })
    }
  }

  return discounts
}

export function calculateFormDiscounts(formData) {
  const { discounts, organisation: { sfid: orgId } = {} } = formData

  const registeredProducts = processFormData(formData)

  const bundleDiscounts = calculateBundleDiscounts(discounts, registeredProducts)
  const multiQtyDiscounts = calculateMultiQtyDiscounts(discounts, registeredProducts, orgId)
  const singleQtyDiscounts = calculateSingleQtyDiscounts(discounts, registeredProducts, orgId)

  const allDiscounts = [...bundleDiscounts, ...multiQtyDiscounts, ...singleQtyDiscounts]

  const allDiscountedProducts = allDiscounts.reduce((acc, discount) => {
    const { discountedProducts = [] } = discount
    acc.push(...discountedProducts)
    return acc
  }, [])

  const registeredProductKeys = getUniqueProperties(registeredProducts, 'registrationKey')

  const discountedProducts = []
  registeredProductKeys.forEach(registrationKey => {
    const productKeys = getUniqueProperties(registeredProducts, 'productKey')

    productKeys.forEach(productKey => {
      const registrationProductDiscounts = allDiscountedProducts.filter(
        p => p.registrationKey === registrationKey && p.productKey === productKey
      )

      registrationProductDiscounts.sort((a, b) => {
        // sort by priority
        if (a.priority < b.priority) return -1
        if (a.priority > b.priority) return 1

        // then by amount
        if (a.discountAmount > b.discountAmount) return -1
        if (a.discountAmount < b.discountAmount) return 1

        // then by discount id (latest created)
        if (a.discount.id > b.discount.id) return -1
        if (a.discount.id < b.discount.id) return 1

        return 0
      })

      if (registrationProductDiscounts.length > 0) {
        discountedProducts.push(registrationProductDiscounts[0])
      }
    })
  })

  const discountIds = getUniqueProperties(discountedProducts, 'discountId')
  const appliedDiscounts = []

  discountIds.forEach(discountId => {
    const discount = allDiscounts.find(d => d.id === discountId)
    const { id, name, description, discountType, subType } = discount

    const discountProducts = discountedProducts.filter(p => p.discount.id === discountId)

    const registrationsSet = new Set()
    const productsSet = new Set()
    let totalAmount = 0

    discountProducts.forEach(product => {
      const { registrationKey, discountAmount } = product
      registrationsSet.add(registrationKey)
      productsSet.add(product.name)
      totalAmount += discountAmount
    })

    const registrationCount = registrationsSet.size
    const products = Array.from(productsSet)

    appliedDiscounts.push({
      id,
      name,
      description,
      discountType,
      subType,
      summary: {
        products,
        registrationCount,
        totalAmount,
      },
    })
  })

  const getProductDiscount = (registrationKey, productKey) => {
    const productDiscount = discountedProducts.find(
      p => p.registrationKey === registrationKey && p.productKey === productKey
    )
    return productDiscount || null
  }

  const appliedBundleDiscounts = appliedDiscounts.filter(d => d.discountType === 'Bundle')

  return {
    registeredProducts,
    allDiscounts,
    allDiscountedProducts,
    appliedDiscounts,
    appliedBundleDiscounts,
    discountedProducts,
    getProductDiscount,
  }
}

function processFormData(formData) {
  const {
    registrations,
    discounts = [],
    registeredTeam,
    isTeamRego,
    productList,
    organisation: { sfid: orgId },
  } = formData

  const discountedProducts = discounts.reduce((acc, discount) => {
    const { products = [], productGroups = [] } = discount

    acc.push(...products.map(p => p.id))

    if (productGroups.length > 0) {
      productGroups.forEach(({ groupProducts = [] }) => {
        acc.push(...groupProducts)
      })
    }

    return acc
  }, [])

  let registeredProducts = []
  if (isTeamRego) {
    const acc = []
    const { teamProducts, teamSubEventProducts, teamAddOn } = registeredTeam || {}

    const allProducts = {
      ...teamProducts,
      ...teamSubEventProducts,
      ...teamAddOn,
    }

    Object.keys(allProducts).forEach(productKey => {
      const product = allProducts[productKey]
      const { checked, quantity: productQuantity } = product || {}

      if (!checked) {
        return
      }

      const productData = productList.find(p => p.sfid === productKey)
      const { id, name, type, price: productPrice, definedById } = productData || {}

      const price = productPrice ? Number(productPrice) : 0
      const quantity = productPrice ? Number(productQuantity) : 0

      const totalPrice = quantity * price

      if (checked && definedById === orgId) {
        acc.push({
          id,
          productKey,
          registrationKey: 'teamRegistration',
          name,
          type,
          definedById,
          // biome-ignore lint/suspicious/noGlobalIsNan: TODO
          price: !isNaN(price) ? price : 0,
          // biome-ignore lint/suspicious/noGlobalIsNan: TODO
          quantity: !isNaN(quantity) ? quantity : 0,
          // biome-ignore lint/suspicious/noGlobalIsNan: TODO
          totalPrice: !isNaN(totalPrice) ? totalPrice : 0,
        })
      }
    })
    registeredProducts = acc
  } else {
    registeredProducts = Object.keys(registrations).reduce((acc, registrationKey) => {
      const registration = registrations[registrationKey]
      const { newProducts, newSubEventProducts, newAddOnProducts } = registration || {}

      const allProducts = {
        ...newProducts,
        ...newSubEventProducts,
        ...newAddOnProducts,
      }

      Object.keys(allProducts).forEach(productKey => {
        const product = allProducts[productKey]
        const { checked, quantity: productQuantity } = product || {}

        if (!checked) {
          return
        }

        const productData = productList.find(p => p.sfid === productKey)
        const { id, name, type, price: productPrice, definedById } = productData || {}

        const price = productPrice ? Number(productPrice) : 0
        const quantity = productPrice ? Number(productQuantity) : 0

        const totalPrice = quantity * price

        if (checked && (definedById === orgId || discountedProducts.includes(id))) {
          acc.push({
            id,
            registrationKey,
            productKey,
            name,
            type,
            definedById,
            // biome-ignore lint/suspicious/noGlobalIsNan: TODO
            price: !isNaN(price) ? price : 0,
            // biome-ignore lint/suspicious/noGlobalIsNan: TODO
            quantity: !isNaN(quantity) ? quantity : 0,
            // biome-ignore lint/suspicious/noGlobalIsNan: TODO
            totalPrice: !isNaN(totalPrice) ? totalPrice : 0,
          })
        }
      })

      return acc
    }, [])
  }

  return registeredProducts
}

export function calculateBundleDiscounts(discounts, registeredProducts) {
  const bundleDiscounts = discounts.filter(d => d.discountType === 'Bundle')
  const calculatedDiscounts = []

  bundleDiscounts.forEach(discount => {
    const {
      id: discountId,
      name: discountName,
      discountType,
      subType,
      discountBy,
      productBy,
      priceCapAmount,
    } = discount
    const allApplicableProducts = []
    const discountedProducts = []
    let discountApplicable = true
    let totalDiscount = 0
    let totalQty = 0
    let totalBundlePrice = 0
    let remainingDiscount = 0
    let remainingQty = 0
    let percentage = 0

    const calculateDiscountAmounts = registration => {
      const { quantity, price } = registration

      if (price === 0) {
        return 0
      }

      // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
      const amountsMapping = amountKeys.map((key, i) => discount[key] || 0)

      // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
      const percentageMapping = percentageKeys.map((key, i) => discount[key] || 0)

      let discountAmount = 0

      for (let i = 0; i < quantity; i++) {
        remainingQty++
        switch (discountBy) {
          case 'Amount': {
            discountAmount = amountsMapping[0]
            break
          }
          case 'Percentage': {
            percentage = percentageMapping[0]
            discountAmount = (percentage / 100) * price
            break
          }
        }

        totalDiscount = discountAmount
      }
    }

    const applyDiscount = registration => {
      const { totalPrice = 0, quantity } = registration || {}

      let discountAmount =
        discountBy === 'Percentage' ? (percentage / 100) * totalPrice : (remainingDiscount / remainingQty) * quantity

      if (discountAmount === 0) {
        return
      }

      if (discountAmount > totalPrice) {
        discountAmount = totalPrice
      }

      remainingDiscount -= discountAmount
      remainingQty -= quantity

      const discountedPrice = totalPrice - discountAmount

      discountedProducts.push({
        ...registration,
        discountId,
        discountName,
        discountType,
        subType,
        discountAmount,
        discountedPrice: discountedPrice > 0 ? discountedPrice : 0,
        priority: 1,
        discount,
      })
    }

    if (productBy === 'ProductGroups') {
      const { productGroups = [] } = discount

      productGroups.forEach(productGroup => {
        if (!discountApplicable) return

        const { minimumPurchaseQty = 1, maximumPurchaseQty, groupProducts = [] } = productGroup

        const applicableProducts = registeredProducts.filter(p => groupProducts.includes(p.id))

        const applicableProductQty = applicableProducts.reduce((acc, p) => {
          acc += p.quantity
          return acc
        }, 0)

        if (applicableProductQty < minimumPurchaseQty) {
          if (productGroups.length > 1) {
            discountApplicable = false
          }
          return
        }

        if (maximumPurchaseQty && applicableProductQty > maximumPurchaseQty) {
          if (productGroups.length > 1) {
            discountApplicable = false
          }
          return
        }

        allApplicableProducts.push(...applicableProducts)
      })
    }

    if (productBy === 'SpecificProducts') {
      const { products = [] } = discount

      products.forEach(product => {
        if (!discountApplicable) return
        const { id, minimumPurchaseQty = 1, maximumPurchaseQty } = product

        const applicableProducts = registeredProducts.filter(p => p.id === id)

        const applicableProductQty = applicableProducts.reduce((acc, p) => {
          acc = p.price !== 0 ? acc + p.quantity : acc + 1
          return acc
        }, 0)

        if (applicableProductQty < minimumPurchaseQty) {
          discountApplicable = false
          return
        }

        if (maximumPurchaseQty && applicableProductQty > maximumPurchaseQty) {
          discountApplicable = false
          return
        }

        allApplicableProducts.push(...applicableProducts)
      })
    }

    if (!discountApplicable || allApplicableProducts.length === 0) {
      return
    }

    allApplicableProducts.forEach(product => {
      totalQty += product.quantity
      totalBundlePrice += product.totalPrice
    })

    allApplicableProducts.sort((a, b) => {
      if (a.totalPrice < b.totalPrice) return -1
      if (a.totalPrice > b.totalPrice) return 1
      return 0
    })

    switch (discountBy) {
      case 'Amount':
      case 'Percentage': {
        allApplicableProducts.forEach(product => calculateDiscountAmounts(product))
        break
      }
      case 'PriceCap': {
        if (priceCapAmount && priceCapAmount < totalBundlePrice) {
          totalDiscount = totalBundlePrice - priceCapAmount
        }
        break
      }
    }

    remainingDiscount = totalDiscount
    remainingQty = totalQty

    allApplicableProducts.forEach(product => {
      applyDiscount(product)
    })

    if (discountedProducts.length > 0) {
      calculatedDiscounts.push({
        ...discount,
        discountedProducts,
        totalDiscount,
      })
    }
  })

  return calculatedDiscounts
}

export function calculateMultiQtyDiscounts(discounts, registeredProducts, orgId) {
  const multiQtyDiscounts = discounts.filter(
    ({ discountType, subType }) => discountType === 'Standard' && subType === 'MultipleQuantity'
  )
  const calculatedDiscounts = []

  multiQtyDiscounts.forEach(discount => {
    const {
      id: discountId,
      name: discountName,
      discountType,
      subType,
      productBy,
      discountBy,
      allSubsequentPurchaseAmount,
      allSubsequentPurchasePercentage,
    } = discount
    const discountedProducts = []
    const totalDiscount = []
    const totalPercentage = []

    // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
    const amountsMapping = amountKeys.map((key, i) => discount[key] || allSubsequentPurchaseAmount || 0)

    // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
    const percentageMapping = percentageKeys.map((key, i) => discount[key] || allSubsequentPurchasePercentage || 0)

    const processApplicableProducts = products => {
      const allRegProducts = calculateAllRegistrationProducts(products)

      allRegProducts.forEach(registration => {
        calculateDiscountAmounts(registration)
      })

      allRegProducts.forEach(registration => {
        applyDiscount(registration)
      })
    }

    const calculateAllRegistrationProducts = products => {
      const allRegProducts = []
      /* eslint-disable-next-line no-restricted-syntax, guard-for-in */
      for (const key in products) {
        const count = products
          .filter(p => products[key].id === p.id)
          .map(m => m.quantity)
          .reduce((a, k) => a + k, 0)
        allRegProducts.push({
          ...products[key],
          quantityTotal: count,
        })
      }
      return allRegProducts
    }

    const calculateDiscountAmounts = registration => {
      const { quantityTotal, price, totalPrice, id } = registration
      let percentage = ''

      if (price === 0) {
        return 0
      }

      let discountAmount = 0
      switch (discountBy) {
        case 'Amount': {
          discountAmount = quantityTotal <= 7 ? amountsMapping[quantityTotal - 1] : amountsMapping[6]
          break
        }
        case 'Percentage': {
          percentage = quantityTotal <= 7 ? percentageMapping[quantityTotal - 1] : percentageMapping[6]
          discountAmount = (percentage / 100) * totalPrice
          break
        }
      }

      totalDiscount.push({ id, discountAmount })
      totalPercentage.push({ id, percentage, discountAmount })
    }

    const applyDiscount = registration => {
      const { totalPrice = 0, quantity, quantityTotal, id } = registration || {}
      const { discountAmount = 0 } = (totalDiscount || []).find(t => t.id === id) || {}
      const { percentage = 0 } = (totalPercentage || []).find(t => t.id === id) || {}

      let newDiscountAmount =
        discountBy === 'Amount' ? (discountAmount / quantityTotal) * quantity : (percentage / 100) * totalPrice

      if (discountAmount === 0) {
        return
      }

      if (newDiscountAmount > totalPrice) {
        newDiscountAmount = totalPrice
      }

      const discountedPrice = totalPrice - discountAmount

      discountedProducts.push({
        ...registration,
        discountId,
        discountName,
        discountType,
        subType,
        discountAmount: newDiscountAmount,
        discountedPrice: discountedPrice > 0 ? discountedPrice : 0,
        priority: 2,
        discount,
      })
    }

    if (productBy === 'ActiveProducts') {
      const orgProducts = orgId ? registeredProducts.filter(r => r.definedById === orgId) : registeredProducts
      processApplicableProducts(orgProducts)
    }

    if (productBy === 'SpecificProducts') {
      const { products = [] } = discount

      products.forEach(product => {
        const { id } = product
        const applicableProducts = registeredProducts.filter(p => p.id === id)
        processApplicableProducts(applicableProducts)
      })
    }

    if (productBy === 'ProductType') {
      const { productType } = discount
      const applicableProducts = registeredProducts.filter(p => p.type === productType)
      processApplicableProducts(applicableProducts)
    }

    if (discountedProducts.length > 0) {
      calculatedDiscounts.push({
        ...discount,
        discountedProducts,
        totalDiscount,
      })
    }
  })

  return calculatedDiscounts
}

export function calculateSingleQtyDiscounts(discounts, registeredProducts, orgId) {
  const singleQtyDiscounts = discounts.filter(
    ({ discountType, subType }) => discountType === 'Standard' && subType === 'SingleQuantity'
  )
  const calculatedDiscounts = []

  singleQtyDiscounts.forEach(discount => {
    const { id: discountId, name: discountName, discountType, subType, productBy } = discount
    const discountedProducts = []
    let totalDiscount = 0

    const calculateDiscountAmount = registration => {
      const { discountBy, amount, percentage } = discount
      const { price, quantity = 1 } = registration

      if (price === 0) {
        return 0
      }

      let discountAmount = 0
      switch (discountBy) {
        case 'Amount': {
          discountAmount = amount * quantity
          break
        }
        case 'Percentage': {
          discountAmount = (percentage / 100) * price * quantity
          break
        }
      }

      return discountAmount
    }

    const applyDiscount = registration => {
      const { totalPrice = 0 } = registration || {}
      let discountAmount = calculateDiscountAmount(registration)

      if (discountAmount === 0) {
        return
      }

      discountAmount = discountAmount > totalPrice ? totalPrice : discountAmount

      totalDiscount += discountAmount

      const discountedPrice = totalPrice - discountAmount

      discountedProducts.push({
        ...registration,
        discountId,
        discountName,
        discountType,
        subType,
        discountAmount,
        discountedPrice: discountedPrice > 0 ? discountedPrice : 0,
        priority: 3,
        discount,
      })
    }

    if (productBy === 'ActiveProducts') {
      const orgProducts = orgId ? registeredProducts.filter(r => r.definedById === orgId) : registeredProducts
      orgProducts.forEach(registration => {
        applyDiscount(registration)
      })
    }

    if (productBy === 'SpecificProducts') {
      const { products = [] } = discount

      products.forEach(product => {
        const { id, minimumPurchaseQty = 1 } = product

        const applicableProducts = registeredProducts.filter(p => p.id === id)
        const applicableProductQty = applicableProducts.reduce((acc, p) => {
          acc += p.quantity
          return acc
        }, 0)

        if (applicableProductQty >= minimumPurchaseQty) {
          applicableProducts.forEach(registration => {
            applyDiscount(registration)
          })
        }
      })
    }

    if (productBy === 'ProductType') {
      const { productType } = discount

      const applicableProducts = registeredProducts.filter(p => p.type === productType)

      applicableProducts.forEach(registration => {
        applyDiscount(registration)
      })
    }

    if (discountedProducts.length > 0) {
      calculatedDiscounts.push({
        ...discount,
        discountedProducts,
        totalDiscount,
      })
    }
  })

  return calculatedDiscounts
}

export function getUniqueProperties(data, key) {
  return Array.from(
    data.reduce((acc, item) => {
      // biome-ignore lint/complexity/useOptionalChain: TODO
      if (item && item[key]) {
        acc.add(item[key])
      }
      return acc
    }, new Set())
  )
}

export function calculateSummaryDiscounts(data) {
  const { products = [], discounts = [] } = data
  const registeredProducts = products

  const bundleDiscounts = calculateBundleDiscounts(discounts, registeredProducts)
  const multiQtyDiscounts = calculateMultiQtyDiscounts(discounts, registeredProducts)
  const singleQtyDiscounts = calculateSingleQtyDiscounts(discounts, registeredProducts)

  const allDiscounts = [...bundleDiscounts, ...multiQtyDiscounts, ...singleQtyDiscounts]
  const allDiscountedProducts = allDiscounts.reduce((acc, discount) => {
    const { discountedProducts = [] } = discount
    acc.push(...discountedProducts)
    return acc
  }, [])

  const registeredProductKeys = getUniqueProperties(registeredProducts, 'registrationKey')

  const discountedProducts = []
  registeredProductKeys.forEach(registrationKey => {
    const productKeys = getUniqueProperties(registeredProducts, 'productKey')

    productKeys.forEach(productKey => {
      const registrationProductDiscounts = allDiscountedProducts.filter(
        p => p.registrationKey === registrationKey && p.productKey === productKey
      )

      registrationProductDiscounts.sort((a, b) => {
        // sort by priority
        if (a.priority < b.priority) return -1
        if (a.priority > b.priority) return 1

        // then by amount
        if (a.discountAmount > b.discountAmount) return -1
        if (a.discountAmount < b.discountAmount) return 1

        // then by discount id (latest created)
        if (a.discount.id > b.discount.id) return -1
        if (a.discount.id < b.discount.id) return 1

        return 0
      })

      if (registrationProductDiscounts.length > 0) {
        discountedProducts.push(registrationProductDiscounts[0])
      }
    })
  })

  const discountIds = getUniqueProperties(discountedProducts, 'discountId')
  const appliedDiscounts = []

  discountIds.forEach(discountId => {
    const discount = allDiscounts.find(d => d.id === discountId)
    const { id, name, description, discountType, subType } = discount

    const discountProducts = discountedProducts.filter(p => p.discount.id === discountId)

    const registrationsSet = new Set()
    const productsSet = new Set()
    let totalAmount = 0

    discountProducts.forEach(product => {
      const { registrationKey, discountAmount } = product
      registrationsSet.add(registrationKey)
      productsSet.add(product.name)
      totalAmount += discountAmount
    })

    const registrationCount = registrationsSet.size
    const products = Array.from(productsSet)

    appliedDiscounts.push({
      id,
      name,
      description,
      discountType,
      subType,
      summary: {
        products,
        registrationCount,
        totalAmount,
      },
    })
  })

  const getProductDiscount = (registrationKey, productKey) => {
    const productDiscount = discountedProducts.find(
      p => p.registrationKey === registrationKey && p.productKey === productKey
    )
    return productDiscount || null
  }

  const appliedBundleDiscounts = appliedDiscounts.filter(d => d.discountType === 'Bundle')

  return {
    registeredProducts,
    allDiscounts,
    allDiscountedProducts,
    appliedDiscounts,
    appliedBundleDiscounts,
    discountedProducts,
    getProductDiscount,
  }
}
